import { Menu, Moon, Search, Sun, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { Botao } from '../components/ui';
import { useSessao } from '../features/auth/contexto';
import { ID_BUSCA_GESTANTE } from '../features/pacientes/BuscaGestante';
import { IndicadorSaude } from '../features/sistema/IndicadorSaude';
import { ambienteAtual } from '../lib/ambiente';
import { cn } from '../lib/cn';
import { useTema } from '../lib/tema';
import { AvisoInatividade } from './AvisoInatividade';
import { AvisoInstalacao } from './AvisoInstalacao';
import { MenuLateral } from './MenuLateral';
import { MenuUsuario } from './MenuUsuario';

/** D-012: aviso fixo de que a homologação usa apenas dados fictícios. */
export function FaixaAmbiente() {
  if (ambienteAtual() !== 'homologacao') return null;
  return (
    <div
      role="note"
      className="bg-alerta-fundo px-4 py-1.5 text-center text-sm font-semibold text-alerta print:hidden"
    >
      AMBIENTE DE HOMOLOGAÇÃO — DADOS FICTÍCIOS
    </div>
  );
}

export function Layout() {
  const { tema, alternar } = useTema();
  const { pathname } = useLocation();

  // Menu do celular/tablet: guarda EM QUAL PÁGINA foi aberto. Ao trocar de página,
  // a comparação deixa de bater e o menu fecha sozinho (sem efeito extra).
  const [menuAbertoEm, setMenuAbertoEm] = useState<string | null>(null);
  const menuAberto = menuAbertoEm === pathname;
  const setMenuAberto = (aberto: boolean) => setMenuAbertoEm(aberto ? pathname : null);

  const { pode } = useSessao();
  const navegar = useNavigate();
  const buscaGestante = pode('pacientes:ver');

  // Esc fecha o menu; ⌘K / Ctrl+K leva à busca de gestante de qualquer tela
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuAbertoEm(null);
      // ? abre a ajuda desta tela (ou a central), fora de campos de texto (D-062)
      const alvo = e.target as HTMLElement | null;
      const digitando =
        alvo?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo?.tagName ?? '');
      if (e.key === '?' && !digitando && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        const botao = document.querySelector<HTMLButtonElement>('[data-ajuda-botao]');
        if (botao) botao.click();
        else void navegar('/ajuda');
      }
      if (buscaGestante && e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const campo = document.getElementById(ID_BUSCA_GESTANTE);
        if (campo) campo.focus();
        else void navegar('/pacientes');
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [buscaGestante, navegar]);

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-primaria focus:px-4 focus:py-2 focus:text-primaria-texto"
      >
        Pular para o conteúdo
      </a>
      <FaixaAmbiente />

      <header className="sticky top-0 z-30 flex h-16 print:hidden items-center gap-3 border-b border-borda bg-superficie px-4">
        <Botao
          variante="fantasma"
          tamanho="icone"
          className="lg:hidden"
          aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuAberto}
          aria-controls="menu-lateral"
          onClick={() => setMenuAberto(!menuAberto)}
        >
          {menuAberto ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </Botao>
        <Link to="/" className="flex items-center gap-2 rounded-md" aria-label="MetaEpi — início">
          <img src="/icone-metaepi.png" alt="" className="size-8 rounded-md bg-white p-0.5" />
          <span className="text-lg font-bold text-primaria">
            Meta<span className="text-sucesso">Epi</span>
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          {buscaGestante && (
            <Link
              to="/pacientes"
              className="flex h-10 items-center gap-2 rounded-lg border border-borda px-3 text-sm text-texto-suave hover:bg-superficie-2"
            >
              <Search className="size-4" aria-hidden="true" />
              <span className="hidden md:inline">Buscar gestante</span>
              <span className="sr-only md:hidden">Buscar gestante</span>
              <kbd className="hidden rounded border border-borda px-1.5 text-xs md:inline">
                Ctrl K
              </kbd>
            </Link>
          )}
          <div className="hidden sm:block">
            <IndicadorSaude />
          </div>
          <Botao
            variante="fantasma"
            tamanho="icone"
            aria-label={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
            onClick={alternar}
          >
            {tema === 'escuro' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
          </Botao>
          <MenuUsuario />
        </div>
      </header>

      <div className="flex flex-1">
        <aside
          id="menu-lateral"
          className={cn(
            'fixed inset-y-0 top-16 left-0 z-20 w-72 overflow-y-auto border-r border-borda bg-superficie transition-transform lg:sticky lg:h-[calc(100vh-4rem)] lg:translate-x-0 print:hidden',
            // fechado no celular: fora da tela E fora da navegação por teclado
            menuAberto ? 'translate-x-0' : '-translate-x-full max-lg:invisible',
          )}
        >
          <MenuLateral aoNavegar={() => setMenuAberto(false)} />
        </aside>
        {menuAberto && (
          <div
            className="fixed inset-0 top-16 z-10 bg-black/40 lg:hidden"
            aria-hidden="true"
            onClick={() => setMenuAberto(false)}
          />
        )}
        <main id="conteudo" tabIndex={-1} className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8 print:p-0">
          <AvisoInstalacao className="mb-4 print:hidden" />
          <Outlet />
        </main>
      </div>
      <AvisoInatividade />
    </div>
  );
}
